import React, { ReactElement, useState } from "react";
import { IntercomProvider } from "react-use-intercom";

interface IIntercomContextType {
  showIntercomSetupModal: boolean;
  setShowIntercomSetupModal: (show: boolean) => void;
}

interface IIntercomContextProviderProps {
  children: ReactElement;
}

export const IntercomContext = React.createContext<IIntercomContextType>({
  showIntercomSetupModal: false,
  setShowIntercomSetupModal: () => {},
});

export function IntercomContextProvider({
  children,
}: IIntercomContextProviderProps) {
  const [showIntercomSetupModal, setShowIntercomSetupModal] = useState(false);
  const appId = process.env.REACT_APP_INTERCOM_APP_ID;

  // No App ID means Intercom is never initialised — but the provider still
  // mounts.
  //
  // `react-use-intercom` defaults `shouldInitialize` to true, so the previous
  // `|| ""` disabled nothing: it injected the widget script with an empty id and
  // Intercom's own snippet answered "The App ID in your code snippet has not
  // been set" on every boot, production included — a third-party script loaded
  // on every page load to accomplish nothing.
  //
  // The provider cannot simply be dropped when the id is missing: `useIntercom`
  // THROWS outside it ('"useIntercom" must be used within `IntercomProvider`'),
  // and `useIntercomHook` is called unconditionally by the Footer and the mobile
  // navigation menu. Omitting it would turn a console warning into a crash on
  // every chain that has no id — and .env.example lists this id under OPTIONAL,
  // with several chain env files leaving it blank, so "absent" is a state that
  // has to work, not a misconfiguration.
  return (
    <IntercomProvider appId={appId ?? ""} shouldInitialize={Boolean(appId)}>
      <IntercomContext.Provider
        value={{ showIntercomSetupModal, setShowIntercomSetupModal }}
      >
        {children}
      </IntercomContext.Provider>
    </IntercomProvider>
  );
}
